import { ArrowBack, Launch } from '@mui/icons-material';
import { Box, Divider, Grid } from '@mui/material';
import { Button, BrandIcon, Card, Typography, UtilityIcon } from '@uhc-tempo/components';
import '@uhc-tempo/components/tempo.css';
import { IconBook, IconClinic, IconStethoscope, IconPillBot } from '@uhc-tempo/icons';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { FormContext } from '../../../context/Form.context';
import styles from '../../../styles/PageStyles.js';
import theme from '../../../styles/PageTheme';
import './Search.page.css';
import SearchBar from './SearchBar.component';
import { useHistory } from 'react-router-dom';
import { LOGIN_ROUTE } from '../../../constants';
import displayLoadingIndicator from '../../../common/components/LoadingIndicator.components';
import { apm } from '@elastic/apm-rum';

export default function SearchPage(props) {
    const classes = styles(theme);
    const {
        loading,
        setLoading,
        setProvider,
        currentSelectedServiceForDisplay,
        setCurrentSelectedServiceForDisplay,
    } = props;
    const history = useHistory();
    const isEstimateScreen = true;

    // Manual page load apm transaction, used for page load metrics
    const transaction = apm.startTransaction('Search Page', 'Component Page Load');

    const {
        memberDetails,
        setEstimateResultsPage,
        setVisionEstimateResponse,
        getCorID,
        sourceSystem,
    } = useContext(FormContext);

    const population = memberDetails?.popConfig?.population;
    // Add the correlationId as a new label to the apm transaction
    transaction.addLabels({
        correlationId: getCorID(),
        userdetails: `${getCorID()}-${sourceSystem}`,
        population: population,
    });

    const [pageLoad, setPageLoad] = useState(false);
    const [showOptumRXPanel, setShowOptumRXPanel] = useState(false);

    const headerRef = useRef(null);

    useEffect(() => {
        if (!pageLoad) {
            headerRef.current.focus(); //focus on header only on page load
            setPageLoad(true);
        }
        setEstimateResultsPage(0);
        setVisionEstimateResponse(0); // clearing the visionEstimates cache
        const coverageTypes = memberDetails?.eligibility?.[0]?.memberHealthCoverage?.coverageType;
        if (coverageTypes?.length > 0) {
            const rxCoverageType = coverageTypes.filter(
                (coverageType) => coverageType?.typeCode?.coverageTypeCode === 'RX'
            );
            setShowOptumRXPanel(rxCoverageType?.length > 0);
        }
        transaction.end();
    }, []);

    const goToOptumRx = () => {
        const url = process.env.OPTUM_RX_URL;
        window.open(url, '_blank');
    };

    return (
        <Box sx={{ width: '100%' }} data-testid="searchPage">
            {loading ? (
                displayLoadingIndicator(isEstimateScreen)
            ) : (
                <Grid container direction="column">
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item>
                                <UtilityIcon size="medium" color="primary" icon={ArrowBack} />
                            </Grid>
                            <Grid item>
                                <Typography.Link
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        history.push(LOGIN_ROUTE);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key == 'Enter') {
                                            history.push(LOGIN_ROUTE);
                                        }
                                    }}
                                    tabIndex="0"
                                >
                                    Start over for a different person
                                </Typography.Link>
                            </Grid>
                        </Grid>
                    </Grid>
                    <div ref={headerRef} tabIndex="-1">
                        <Grid item xs={12}>
                            <Typography.H1 align="left" data-testid="searchPageHeader">
                                Estimate your cost for upcoming care
                            </Typography.H1>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={classes.divider} data-testid="headerDivider"></Divider>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography.H6
                                style={{
                                    fontWeight: 'bold',
                                    color: 'black',
                                    marginBottom: '20px',
                                }}
                            >
                                Let's get started. This estimate is for{' '}
                                {`${memberDetails?.demographics?.name?.firstName} ${memberDetails?.demographics?.name?.lastName}`}
                            </Typography.H6>
                        </Grid>
                    </div>
                    <SearchBar
                        loading={loading}
                        setLoading={setLoading}
                        setProvider={setProvider}
                        currentSelectedServiceForDisplay={currentSelectedServiceForDisplay}
                        setCurrentSelectedServiceForDisplay={setCurrentSelectedServiceForDisplay}
                    />
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 1,
                                display: 'grid',
                                gridTemplateColumns: { md: '1fr 1fr 1fr' },
                                gap: 2,
                                marginTop: 3,
                            }}
                        >
                            <Card border={true} layout="custom" className="card">
                                <Grid container direction="row" alignItems="center">
                                    <Grid item mr={1}>
                                        <BrandIcon
                                            icon={IconStethoscope}
                                            size="EXTRA_SMALL"
                                            styleVariation="TWO_TONE_LIGHT_CIRCLE"
                                        />
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography.H6 className="tds-margin-none textColorBlack">
                                            Procedures, Services or treatments
                                        </Typography.H6>
                                    </Grid>
                                </Grid>
                                <Typography.Paragraph bodyStyle={2} className="textColorBlack">
                                    Procedures, Services and Treatments are medically prescribed
                                    care to treat or a resolve a one-time or ongoing condition you
                                    are experiencing.
                                </Typography.Paragraph>
                                <Typography.Paragraph bodyStyle={2} className="textColorBlack">
                                    Some examples are: Knee Surgery,physical therapy, mental health
                                    counseling, chemotherapy, acid reflux, bronchitis, broken bone,
                                    MRI, X-ray, etc.
                                </Typography.Paragraph>
                            </Card>

                            <Card border={true} layout="custom" className="card">
                                <Grid container direction="row" alignItems="center">
                                    <Grid item mr={1}>
                                        <BrandIcon
                                            icon={IconBook}
                                            size="EXTRA_SMALL"
                                            styleVariation="TWO_TONE_LIGHT_CIRCLE"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Typography.H6 className="tds-margin-none textColorBlack">
                                            Billing codes
                                        </Typography.H6>
                                    </Grid>
                                </Grid>
                                <Typography.Paragraph bodyStyle={2} className="textColorBlack">
                                    Billing codes are assigned to medical services and treatments in
                                    order to assure that we're all talking about the same thing when
                                    we say "knee surgery," for example.
                                </Typography.Paragraph>
                                <Typography.Paragraph bodyStyle={2} className="textColorBlack">
                                    The most common of these is a CPT code. To ensure that your
                                    estimate is accurate, it would be helpful to have CPT codes.
                                </Typography.Paragraph>
                            </Card>

                            <Card border={true} layout="custom" className="card">
                                <Grid container direction="row" alignItems="center">
                                    <Grid item mr={1}>
                                        <BrandIcon
                                            icon={IconClinic}
                                            size="EXTRA_SMALL"
                                            styleVariation="TWO_TONE_LIGHT_CIRCLE"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Typography.H6 className="tds-margin-none textColorBlack">
                                            Facility
                                        </Typography.H6>
                                    </Grid>
                                </Grid>
                                <Typography.Paragraph bodyStyle={2} className="textColorBlack">
                                    {' '}
                                    <span className="tds-text__paragraph--bold">
                                        Facilities
                                    </span>{' '}
                                    offer access to same day surgeries, preventive medicine,
                                    counseling, wellness screenings and more.
                                </Typography.Paragraph>
                                <Typography.Paragraph bodyStyle={2} className="textColorBlack">
                                    <span className="tds-text__paragraph--bold">
                                        Lab facilities
                                    </span>{' '}
                                    perform testing (blood, fecal, saliva, etc.) for Mono, Strep,
                                    Liver Enzymes, Lipids, Thyroid hormone levels and more.
                                </Typography.Paragraph>
                            </Card>

                            {showOptumRXPanel && (
                                <Card border={true} layout="custom" className="card">
                                    <Grid container direction="row" alignItems="center">
                                        <Grid item mr={1}>
                                            <BrandIcon
                                                icon={IconPillBot}
                                                size="EXTRA_SMALL"
                                                styleVariation="TWO_TONE_LIGHT_CIRCLE"
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography.H6 className="tds-margin-none textColorBlack">
                                                Optum RX
                                            </Typography.H6>
                                        </Grid>
                                    </Grid>
                                    <Grid>
                                        <Typography.Paragraph
                                            bodyStyle={2}
                                            className="textColorBlack"
                                        >
                                            With OptumRX as your pharmacy you can get Pharmacy &
                                            Prescription medication estimates, manage your
                                            prescriptions online, and receive free home delivery.
                                        </Typography.Paragraph>
                                    </Grid>
                                    <Grid container justifyContent="flex-end">
                                        <Button
                                            style={{
                                                width: '210px',
                                                borderRadius: '24px',
                                                position: 'relative',
                                                top: '-7px',
                                                whiteSpace: 'nowrap',
                                                minWidth: 'auto',
                                                marginTop: '10px',
                                            }}
                                            role="link"
                                            aria-label="Go to Optum RX, opens in a new tab or window"
                                            data-testid="gotoOptumRxButton"
                                            onClick={() => goToOptumRx()}
                                        >
                                            Go to Optum RX
                                            <Launch
                                                style={{
                                                    height: '15px',
                                                    width: '15px',
                                                    marginLeft: '5px',
                                                    position: 'absolute',
                                                    marginTop: '4px',
                                                }}
                                            />
                                        </Button>
                                    </Grid>
                                </Card>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
